<template>
  <div ref="component" class="sp-expandable-list" :class="classModifiers">
    <div ref="content" class="sp-expandable-list__content" :style="contentCSSProperties">
      <slot />
    </div>
    <div v-if="showActivator" class="sp-expandable-list__activator" @click="onActivatorClick">
      <slot name="activator">
        <sp-button
          ref="activatorButton"
          :data-testid="activatorTestId"
          :label="buttonLabel"
          :variant="variant"
          :size="activatorSize"
        ></sp-button>
      </slot>
    </div>
  </div>
</template>

<script setup>
/**
 * The SpExpandableList component is used to display a list of items that can be expanded or collapsed.
 *
 * @displayName ExpandableList
 * @group Custom Elements
 * @component sp-expandable-list
 */
import { toBoolean } from "#ce/utils/props";
import { props as expandableListProps } from "./props.js";
import { onClickOutside } from "@vueuse/core";
import { computed, onMounted, ref, useAttrs, useTemplateRef, watch } from "vue";

defineOptions({
  inheritAttrs: false,
});

const emit = defineEmits(["update-expanded"]);

const props = defineProps(expandableListProps);
const attrs = useAttrs();

const isExpanded = ref(toBoolean(props.expanded));
watch(isExpanded, (value) => emit("update-expanded", value));

watch(
  () => props.expanded,
  (value) => (isExpanded.value = toBoolean(value)),
);

const component = useTemplateRef("component");
onClickOutside(component, () => (isExpanded.value = false));

const buttonLabel = computed(() => (isExpanded.value ? props.expandedTitle : (attrs.title ?? "Show more")));

const contentCSSProperties = computed(() => ({
  "--expandable-list-columns": props.columns > 0 ? props.columns : undefined,
}));

const content = useTemplateRef("content");
const listItems = computed(() => {
  if (content.value?.firstElementChild?.tagName === "SLOT") {
    return content.value.firstElementChild.assignedElements();
  }

  return Array.from(content.value?.children ?? []);
});
const listItemsCount = computed(() => listItems.value.length);

const hasLimitDefined = computed(() => props.limit !== undefined && props.limit > 0);

const exceedsLimit = computed(() => hasLimitDefined.value && listItemsCount.value > props.limit);

const activatorButton = useTemplateRef("activatorButton");
const showActivator = computed(() => exceedsLimit.value);

const classModifiers = computed(() => ({
  "--is-expanded": isExpanded.value,
  "--fade-last-item-on-collapse": toBoolean(props.fadeLastItemOnCollapse),
}));

function onActivatorClick() {
  isExpanded.value = !isExpanded.value;
  activatorButton.value?.blur();
}

function adjustListItemsVisibility() {
  if (!exceedsLimit.value) {
    return;
  }

  listItems.value.slice(props.limit).forEach((item) => {
    item.classList.toggle("sp-expandable-list__item--hidden", !isExpanded.value);
  });
}

watch(
  () => [listItemsCount.value, isExpanded.value],
  () => adjustListItemsVisibility(),
  { immediate: true },
);

onMounted(async () => adjustListItemsVisibility());
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
::slotted(.sp-expandable-list__item--hidden) {
  display: none !important;
}

.sp-expandable-list {
  position: relative;
  --content-z-index: var(--sp-comp-expandable-list-content-z-index, 1);

  &.--fade-last-item-on-collapse:not(.--is-expanded) {
    .sp-expandable-list__activator {
      padding-top: var(--sp-comp-expandable-list-activator-padding-top, 2rem);
      z-index: calc(var(--content-z-index) + 1);
      width: 100%;
      position: absolute;
      bottom: var(--sp-comp-expandable-list-activator-bottom, 0);
      background: linear-gradient(
        to bottom,
        transparent,
        white var(--sp-comp-expandable-list-activator-background-color-end, 60%)
      );
    }
  }
}

.sp-expandable-list__content {
  --expandable-list-columns: 1;
  --expandable-list-gap: 15px;

  display: grid;
  grid-auto-rows: auto;
  gap: var(--expandable-list-gap);
  grid-template-columns: repeat(var(--expandable-list-columns), 1fr);
  z-index: var(--content-z-index);
}

sp-button[variant="outlined"] {
  margin-top: var(--sp-comp-expandable-list-variant-outlined-activator-gap, 0.75rem);
  --sp-comp-button-font-size: var(
    --sp-comp-expandable-list-variant-outlined-activator-font-size,
    var(--sp-sys-body-font-size, 0.75rem)
  );
}
</style>
